import { captureException } from '@sentry/react';
import { getUpNextArticle } from 'data/SingleArticle/data';
import { UpNextArticle } from 'data/SingleArticle/types';
import difference from 'lodash.difference';
import { useEffect, useReducer } from 'react';
import { getParselyRelatedArticlesSlugs } from 'rest/parsely/client';
import { useCookieConsent } from '@zapier/cookies';

type Response = {
  loading: boolean;
  articleSlug: string;
  upNextArticle: UpNextArticle | null;
  error: boolean;
  fetchUpNextArticle: () => void;
};
type Options = {
  slugsToDedupe: string;
  limit?: number;
};
type State = Omit<Response, 'fetchUpNextArticle'>;
type Action =
  | { type: 'loading'; payload: boolean }
  | { type: 'error' }
  | { type: 'success-upNextArticle'; payload: UpNextArticle }
  | { type: 'success-articleSlug'; payload: string };

const initialState = {
  loading: false,
  articleSlug: '',
  upNextArticle: null,
  error: false,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'loading': {
      return { ...state, loading: action.payload };
    }
    case 'error': {
      return { ...state, error: true, loading: false };
    }
    case 'success-upNextArticle': {
      return { ...state, upNextArticle: action.payload, loading: false };
    }
    case 'success-articleSlug': {
      return { ...state, articleSlug: action.payload };
    }
    default: {
      return initialState;
    }
  }
};

export function useUpNextArticles({
  slugsToDedupe,
  limit = 2,
}: Options): Response {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    byName: { analytics: analyticsCookies },
  } = useCookieConsent();

  function fetchUpNextArticle() {
    dispatch({ type: 'loading', payload: true });
  }

  useEffect(() => {
    async function getRelatedArticles() {
      try {
        const parselySlugs = await getParselyRelatedArticlesSlugs({
          limit,
          analyticsCookiesAccepted: analyticsCookies,
        });
        const upNextArticleSlug = difference(parselySlugs, [slugsToDedupe]);

        dispatch({
          type: 'success-articleSlug',
          payload: upNextArticleSlug[0],
        });
      } catch (e) {
        captureException(e);
        dispatch({ type: 'error' });
      }
    }

    if (!state.loading) {
      return;
    }

    getRelatedArticles();
  }, [state.loading]);

  useEffect(() => {
    async function fetchUpNextArticle(slug: string) {
      try {
        const upNextArticle = await getUpNextArticle(slug);

        dispatch({ type: 'success-upNextArticle', payload: upNextArticle });
      } catch (e) {
        captureException(e);
        dispatch({ type: 'error' });
      }
    }
    if (state.error || !state.articleSlug) {
      return;
    }

    fetchUpNextArticle(state.articleSlug);
  }, [state.articleSlug]);

  return {
    articleSlug: state.articleSlug,
    error: state.error,
    fetchUpNextArticle,
    loading: state.loading,
    upNextArticle: state.upNextArticle,
  };
}
